/**
 * The 12 invaders of the GDD (section 6): name, linked material, sensitivities and passives. All have the
 * same weapons and 100 HP. Passives whose material is not built yet (fire, black powder, acid, ice,
 * electricity) are in the data but only take effect with their wave.
 */
import { Physique } from '@invaders/sim';

const { WATER, LAVA, OIL, ACID } = Physique;

/** How water acts on the unit besides drowning (GDD table "Sensibilités des unités", column EAU; drowning: `noDrown`). */
export type WaterEffect = 'none' | 'hurt' | 'heal' | 'defused';
/** How cold acts on the unit (column GEL). */
export type FreezeEffect = 'frozen' | 'immune' | 'healed';

export interface InvaderKind {
  readonly name: string;
  readonly title: string;
  /** Linked material, -1 for none (Zorg) or for an effect that is not a cell (Robotman: electricity). */
  readonly material: number;
  /** Placeholder sprite colour, 0xRRGGBB. */
  readonly color: number;
  /** Damage multipliers in percent: 100 = ×1, 0 = immune. */
  readonly fire: number;
  readonly lava: number;
  readonly acid: number;
  readonly explosion: number;
  readonly water: WaterEffect;
  readonly freeze: FreezeEffect;
  /** Walking speed in percent (Golem: 70). */
  readonly speed: number;
  /** Swimming speed in percent (Fishman: 200). */
  readonly swim: number;
  /** Glides: slow fall, no fall damage (Zorg, Cloudman). */
  readonly glide: boolean;
  /** Jump height in percent (Zorg: 200). */
  readonly jump: number;
  /** Sinks like a stone in liquids (Robotman). */
  readonly heavy: boolean;
  /**
   * The liquids it does not drown in. The author's rule: the head under water, lava, oil or acid kills at once, except
   * in its own matter (Fishman water, Lavaman lava, Lizardman acid), and never Cloudman, a cloud (the GDD: Fishman and
   * Cloudman do not drown, in water).
   */
  readonly noDrown: readonly number[];
}

const BASE = {
  fire: 100, lava: 100, acid: 100, explosion: 100, water: 'none', freeze: 'frozen',
  speed: 100, swim: 100, glide: false, jump: 100, heavy: false, noDrown: [],
} as const;

export const INVADERS: readonly InvaderKind[] = [
  { ...BASE, name: 'Zorg', title: 'Alien', material: -1, color: 0x7fe07a, glide: true, jump: 200 },
  { ...BASE, name: 'Golem', title: 'Rock golem', material: Physique.ROCK, color: 0x8a837c, acid: 150, explosion: 60, speed: 70 },
  { ...BASE, name: 'Sandman', title: 'Man of sand', material: Physique.SAND, color: 0xe2c46a, acid: 150, explosion: 70 },
  { ...BASE, name: 'Fishman', title: 'Fish on legs', material: Physique.WATER, color: 0x4f8fe0, swim: 200, noDrown: [WATER] },
  { ...BASE, name: 'Lavaman', title: 'Lava golem', material: Physique.LAVA, color: 0xff6a2a, fire: 0, lava: 0, water: 'hurt', freeze: 'immune', noDrown: [LAVA] },
  { ...BASE, name: 'Treeman', title: 'Walking tree', material: Physique.WOOD, color: 0x5f9a3a, fire: 200, lava: 200, acid: 150, water: 'heal' },
  { ...BASE, name: 'Robotman', title: 'Robot', material: -1, color: 0xb8c4cc, acid: 0, explosion: 50, heavy: true },
  { ...BASE, name: 'Cloudman', title: 'Man of cloud', material: Physique.STEAM, color: 0xe8eef2, glide: true, noDrown: [WATER, LAVA, OIL, ACID] },
  { ...BASE, name: 'Fireman', title: 'Man of fire', material: Physique.FIRE, color: 0xffb02e, fire: 0, lava: 50, water: 'hurt', freeze: 'immune' },
  { ...BASE, name: 'Blackbeard', title: 'Pirate', material: Physique.BLACK_POWDER, color: 0x4a4a52, water: 'defused' },
  { ...BASE, name: 'Lizardman', title: 'Lizard man', material: Physique.ACID, color: 0x9fe04a, acid: 0, noDrown: [ACID] },
  { ...BASE, name: 'Snowman', title: 'Living snowman', material: Physique.ICE, color: 0xf4fbff, fire: 200, lava: 200, freeze: 'healed' },
];

export const ZORG = 0;
export const GOLEM = 1;
export const SANDMAN = 2;
export const FISHMAN = 3;
export const LAVAMAN = 4;
export const TREEMAN = 5;
export const ROBOTMAN = 6;
export const CLOUDMAN = 7;
export const FIREMAN = 8;
export const BARBE_NOIRE = 9;
export const LIZARDMAN = 10;
export const SNOWMAN = 11;

export function isInvaderKind(kind: number): boolean {
  return Number.isInteger(kind) && kind >= 0 && kind < INVADERS.length;
}

/**
 * Kinds of damage: the sensitivities apply to explosion, fire, lava and acid. 'risingLava' hurts everyone,
 * Lavaman and Fireman included (GDD section 3); the others (direct hits, falls) are never scaled.
 */
export type DamageKind = 'explosion' | 'fire' | 'lava' | 'acid' | 'risingLava' | 'direct' | 'fall' | 'water';

/** Damage after the unit's sensitivity. */
export function scaledDamage(kind: number, damage: DamageKind, amount: number): number {
  const k = INVADERS[kind];
  const pct = damage === 'explosion' ? k.explosion : damage === 'fire' ? k.fire : damage === 'lava' ? k.lava : damage === 'acid' ? k.acid : 100;
  return Math.trunc((amount * pct) / 100);
}
